import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { POPUP_REVEAL_DELAY_MS } from '@/features/home/components/homeHero';
import { fetchHomePopups, homePopupQueryKey } from '@/features/home/popupApi';
import { getTomorrowTimestamp, isHiddenUntilActive } from '@/features/home/utils';
import { useAppLocale } from '@/i18n/useAppLocale';
import { useHomeUiStore } from '@/stores/useHomeUiStore';
import { classNames } from '@/utils/classNames';

import styles from './HomePopup.module.scss';

const POPUP_FADE_REVEAL_DURATION_MS = 24;
const HOME_POPUP_STORAGE_KEY = 'home.notice-hidden-until';

const createPanelStyle = (index: number): CSSProperties => {
  return {
    '--popup-index': index,
  } as CSSProperties;
};

const HomePopup = () => {
  const { locale } = useAppLocale();
  const [shouldHideToday, setShouldHideToday] = useState(false);
  const dismissPopup = useHomeUiStore((state) => state.dismissPopup);
  const finishPopupReveal = useHomeUiStore((state) => state.finishPopupReveal);
  const hidePopupUntilNextEligibilityCheck = useHomeUiStore(
    (state) => state.hidePopupUntilNextEligibilityCheck,
  );
  const introPhase = useHomeUiStore((state) => state.introPhase);
  const popupEligible = useHomeUiStore((state) => state.popupEligible);
  const popupPhase = useHomeUiStore((state) => state.popupPhase);
  const setPopupEligible = useHomeUiStore((state) => state.setPopupEligible);
  const startPopupReveal = useHomeUiStore((state) => state.startPopupReveal);
  const popupQuery = useQuery({
    queryKey: [...homePopupQueryKey, locale],
    queryFn: fetchHomePopups,
  });
  const popups = (popupQuery.data ?? []).slice(0, 3);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setPopupEligible(false);
      return;
    }

    const hiddenUntil = window.localStorage.getItem(HOME_POPUP_STORAGE_KEY);
    setPopupEligible(popups.length > 0 && !isHiddenUntilActive(hiddenUntil));
  }, [popups.length, setPopupEligible]);

  useEffect(() => {
    if (!popupEligible || popupPhase !== 'hidden' || introPhase !== 'complete') {
      return;
    }

    const timerId = window.setTimeout(() => {
      startPopupReveal();
    }, POPUP_REVEAL_DELAY_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [introPhase, popupEligible, popupPhase, startPopupReveal]);

  useEffect(() => {
    if (popupPhase !== 'revealing') {
      return;
    }

    const timerId = window.setTimeout(() => {
      finishPopupReveal();
    }, POPUP_FADE_REVEAL_DURATION_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [finishPopupReveal, popupPhase]);

  useEffect(() => {
    if (popupPhase === 'hidden' || popupPhase === 'dismissed') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (typeof window !== 'undefined' && shouldHideToday) {
          window.localStorage.setItem(HOME_POPUP_STORAGE_KEY, String(getTomorrowTimestamp()));
          dismissPopup();
          return;
        }

        hidePopupUntilNextEligibilityCheck();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dismissPopup, hidePopupUntilNextEligibilityCheck, popupPhase, shouldHideToday]);

  const handleClose = () => {
    if (typeof window !== 'undefined' && shouldHideToday) {
      window.localStorage.setItem(HOME_POPUP_STORAGE_KEY, String(getTomorrowTimestamp()));
      dismissPopup();
      return;
    }

    hidePopupUntilNextEligibilityCheck();
  };

  if (
    !popupEligible ||
    popupPhase === 'hidden' ||
    popupPhase === 'dismissed' ||
    popups.length === 0
  ) {
    return null;
  }

  return (
    <div
      aria-label='홈 팝업'
      aria-modal='true'
      className={classNames(
        styles['overlay'],
        popupPhase === 'visible' && styles['overlayInteractive'],
      )}
      role='dialog'
    >
      {popups.map((popup, index) => (
        <div
          className={classNames(
            styles['panel'],
            popupPhase === 'revealing' && styles['panelRevealing'],
            popupPhase === 'visible' && styles['panelVisible'],
          )}
          key={popup.id}
          style={createPanelStyle(index)}
        >
          <div className={styles['body']}>
            <a
              className={styles['imageLink']}
              href={popup.linkUrl || undefined}
              rel={popup.linkUrl.startsWith('http') ? 'noreferrer' : undefined}
              target={popup.linkUrl.startsWith('http') ? '_blank' : undefined}
            >
              <img
                alt={popup.title}
                src={popup.imageUrl}
                style={{
                  objectFit: popup.imageFit,
                  objectPosition: popup.imageObjectPosition,
                  transform: `scale(${String(popup.imageScale)})`,
                  transformOrigin: popup.imageObjectPosition,
                }}
              />
            </a>
          </div>

          <div className={styles['footer']}>
            <label className={styles['checkboxLabel']}>
              <input
                checked={shouldHideToday}
                onChange={(event) => {
                  setShouldHideToday(event.currentTarget.checked);
                }}
                type='checkbox'
              />
              <span aria-hidden='true' className={styles['checkboxBox']} />
              <span>오늘 열지 않기</span>
            </label>

            <button className={styles['closeButton']} onClick={handleClose} type='button'>
              <span>닫기</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default HomePopup;
